import React from "react";
import styled from "styled-components";
import ReactMarkdown, { defaultUrlTransform, ExtraProps } from "react-markdown";
import { FaPrint } from "react-icons/fa";
import { ElementContent, Root, Properties, Element } from "hast";
// @ts-ignore
import { visit } from "unist-util-visit";
import gfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import { isValidShortcodeComponent } from "../applications/apply-page/apply-page";
import { HyperLink } from "../simple-buttons/simple-buttons";
import { remCalc } from "../theme/style-utils-coras-nua";
import defaultTheme from "../theme/theme-test-override";
import { ThemeTemplate } from "../theme/theme-template";
import {
    Paragraph,
    ProgressStep,
    Heading,
    PcaSubHeading,
    Heading3
} from "../theme/typography";

type BoiMarkdownProps = {
    components?: object;
    linkTreatment?: boolean;
    disallowedElements?: Array<string>;
    unwrapDisallowed?: boolean;
    className?: string;
    children?: string;
    enableRaw?: boolean;
    fieldWidth?: number;
};

const urlTransformTels = (url: string) => {
    if (url && url.startsWith("tel")) {
        return url;
    }
    return defaultUrlTransform(url);
};

const toCamelCase = (str: string) =>
    str.replace(/-([a-z])/g, m => m[1].toUpperCase());

// Converts hyphenated attributes to camelcase for shortcode components
const camelCaseAttributes = () => (tree: Root) => {
    visit(tree, "element", (node: Element) => {
        if (isValidShortcodeComponent(node.tagName)) {
            const originalProps = node.properties;
            const newProps: Properties = {};

            for (const key in originalProps) {
                newProps[toCamelCase(key)] = originalProps[key];
            }

            node.properties = newProps;
        }
    });
};

const BoiMarkdown = (initialProps: BoiMarkdownProps) => {
    const {
        components,
        enableRaw,
        linkTreatment = true,
        ...props
    } = initialProps;
    const rehypePlugins = [];
    enableRaw && rehypePlugins.push(rehypeRaw, camelCaseAttributes);

    return (
        <ReactMarkdown
            remarkPlugins={[gfm]}
            urlTransform={urlTransformTels}
            rehypePlugins={rehypePlugins}
            components={{
                a({
                    node,
                    children,
                    ...props
                }: React.AnchorHTMLAttributes<HTMLAnchorElement> & ExtraProps) {
                    if (linkTreatment) {
                        props["aria-label"] = String(children);

                        if (props.href && !props.href.startsWith("#")) {
                            props.target = "_blank";
                            props.rel = "noopener noreferrer";
                            props["aria-label"] += ", opens in a new tab";
                        }

                        // Print button functionality
                        if (props.href && props.href === "print") {
                            return (
                                <HyperLink
                                    as="a"
                                    className="u-margin-auto"
                                    onClick={() => window.print()}
                                    icon={<FaPrint />}
                                    justifyContent="center"
                                    iconPosition="right"
                                >
                                    {children}
                                </HyperLink>
                            );
                        }
                    }
                    return <a {...props}>{children}</a>;
                },
                p({
                    node,
                    children
                }: React.HTMLAttributes<HTMLParagraphElement> & ExtraProps) {
                    const Component = node?.children.some(
                        (child: ElementContent) =>
                            child.type === "element" &&
                            isValidShortcodeComponent(child.tagName)
                    )
                        ? React.Fragment
                        : Paragraph;

                    return <Component>{children}</Component>;
                },
                table(props) {
                    return (
                        <div className="table-container" tabIndex={0}>
                            <table {...props} />
                        </div>
                    );
                },
                h1: Heading,
                h2: PcaSubHeading,
                h3: Heading3,
                h5: ProgressStep,
                ...components
            }}
            {...props}
        />
    );
};
BoiMarkdown.defaultProps = {
    components: {}
};
type BoiReactMarkdownProps = {
    theme?: ThemeTemplate;
    fieldWidth?: number;
};

const BoiReactMarkdown = styled(BoiMarkdown)<BoiReactMarkdownProps>`
    & .table-container{
        overflow: auto;
        margin-bottom: 1rem;
        table{
            border-collapse: collapse;
            margin-bottom: 1rem;
            width: 100%;
            border: ${remCalc(1)} solid ${props =>
    props.theme.colors.monotoneMidLight};
            background: ${props => props.theme.colors.brilliant};
            td, th{
                padding: ${remCalc(8)};
                border-bottom: ${remCalc(1)} solid ${props =>
    props.theme.colors.monotoneMidLight};
                border-right: ${remCalc(1)} solid ${props =>
    props.theme.colors.monotoneMidLight};
                &:last-child{
                    border-right: 0;
                }
            }
            th{
                border-top: 2px solid ${props => props.theme.colors.primary};
                border-bottom: 1px solid ${props =>
                    props.theme.colors.monotoneMid};
                font-weight: ${props => props.theme.fontWeights.semiBold};
            }
            tr:last-child{
                td{
                    border-bottom: 0;
                }
            }
        }
    }
    & .footnotes {
        font-size: ${props => props.theme.fontSizes[1]};
        border-width: ${remCalc(1)} 0 0;
        border-style: solid;
        padding-top: ${props => props.theme.space[5]};
        margin-top: ${props => props.theme.space[5]};
        color: ${props => props.theme.colors.monotoneMid};
        ol{
            counter-reset: note-counter;
            list-style: none;
            padding-left: 0;
            li{
                margin-bottom: ${props => props.theme.space[5]};
                position: relative;
                font-size: ${props => props.theme.fontSizes[1]};
                &:before {
                    counter-increment: note-counter;
                    content: counter(note-counter);
                    position: absolute;
                    left: 0;
                    background-color: ${props =>
                        props.theme.colors.monotoneMidLight};
                    padding: 0 ${remCalc(5)};
                    top: ${remCalc(2)};
                    line-height: ${props => props.theme.lineHeights[4]}
                    border-radius: ${props => props.theme.radii[1]};
                }
                p{
                    padding-left: ${props => props.theme.space[6]};
                    font-size: ${props => props.theme.fontSizes[1]};
                    margin-bottom: ${props => props.theme.space[3]};
                }
            }
        }

    }
    & .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        word-wrap: normal;
        border: 0;
    }
    & [data-footnote-backref]{
        display: none;
    }
    & [data-footnote-ref]{
        text-decoration: none;
        background-color: ${props => props.theme.colors.monotoneMidLight};
        padding: 0 ${props => props.theme.space[1]};
        margin: 0 0 0 ${props => props.theme.space[1]};
        cursor: help;
        border-radius: ${props => props.theme.radii[1]};
    }
    & sup{
        line-height: ${remCalc(16)};
    }
`;
BoiReactMarkdown.defaultProps = {
    theme: {
        ...defaultTheme
    } as ThemeTemplate,
    components: {}
};
export default BoiReactMarkdown;
